import { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest } from '../utils/apiUtils';
import type { MatchPreviewData } from '@myfutbolapp/shared';

const POLLING_INTERVAL_MS = 10_000;

export interface UseMatchPreviewResult {
  match: MatchPreviewData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useMatchPreview(matchId: string | undefined): UseMatchPreviewResult {
  const [match, setMatch] = useState<MatchPreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchPreview = useCallback(
    async (showLoading = false) => {
      if (!matchId) return;
      if (showLoading) setLoading(true);
      setError(null);
      try {
        const res = await apiRequest<MatchPreviewData>(`/matches/${matchId}/preview`, {
          requireAuth: false,
        });
        if (res.data) {
          setMatch(res.data);
        } else {
          setError(res.error || 'No pudimos cargar el partido');
        }
      } catch (e) {
        setError('No pudimos cargar el partido');
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [matchId]
  );

  useEffect(() => {
    void fetchPreview(true);
  }, [fetchPreview]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (!match) return;
    if (match.status === 'FINISHED' || match.status === 'CANCELLED') return;
    intervalRef.current = setInterval(() => {
      void fetchPreview(false);
    }, POLLING_INTERVAL_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [match, fetchPreview]);

  return { match, loading, error, refetch: () => fetchPreview(false) };
}
